import { useEffect, useRef, useState } from "react";
import FocusTrap from "focus-trap-react";
import {
  getCart,
  removeFromCart,
  clearCart,
  isCartDrawerOpen,
  onCartDrawerChange,
  onCartChange,
  closeCartDrawer,
  addToCart,
} from "./Cart";

export default function CartDrawer() {
  const [open, setOpen] = useState(isCartDrawerOpen());
  const [items, setItems] = useState(getCart());
  const [isProcessing, setIsProcessing] = useState(false);
  const drawerRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    const unsubscribeDrawer = onCartDrawerChange((newState) => {
      setOpen(newState);

      if (newState) {
        setTimeout(() => {
          closeButtonRef.current?.focus();
        }, 0);
      }
    });

    const unsubscribeCart = onCartChange((newItems) => {
      setItems(newItems);
    });

    // Initialize with current state
    setItems(getCart());

    return () => {
      unsubscribeDrawer();
      unsubscribeCart();
    };
  }, []);

  const updateQuantity = (product_id, name, delta) => {
    const current = getCart().find(
      (item) => item.product_id === product_id && item.name === name
    );
    if (!current) return;

    const newQty = current.quantity + delta;
    if (newQty < 1) return;

    removeFromCart(product_id, name);
    addToCart({ ...current, quantity: newQty });
  };

  const handleRemove = (product_id, name) => {
    removeFromCart(product_id, name);
  };

  const handleCheckout = async () => {
    setIsProcessing(true);

    try {
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });

      if (res.ok) {
        const data = await res.json();
        clearCart();
        window.location.href = data.checkout_url;
      } else {
        const text = await res.text();
        console.error("Checkout failed:", res.status, text);
        alert("Checkout failed. Please try again.");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      alert("An unexpected error occurred.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div
      className={`cart-drawer${open ? " open" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cart-title"
      tabIndex={-1}
      ref={drawerRef}
      onClick={closeCartDrawer}
    >
      <FocusTrap
        active={open}
        focusTrapOptions={{
          onDeactivate: closeCartDrawer,
          escapeDeactivates: true,
          clickOutsideDeactivates: true,
        }}
      >
        <div className="cart-inner" onClick={(e) => e.stopPropagation()}>
          <div className="cart-header">
            <h3 id="cart-title">Your Cart</h3>
            <button
              onClick={closeCartDrawer}
              className="cart-close-button"
              aria-label="Close cart"
              ref={closeButtonRef}
            >
              &times;
            </button>
          </div>
          <div className="cart-items">
            {items.length === 0 ? (
              <p className="empty-cart">Your cart is empty.</p>
            ) : (
              items.map((item, index) => (
                <div key={`${item.product_id}-${item.name}-${index}`} className="cart-item">
                  <div className="cart-item-name">{item.name}</div>
                  <div className="quantity-selector">
                    <button
                      onClick={() => updateQuantity(item.product_id, item.name, -1)}
                      className="quantity-button"
                      aria-label={`Decrease quantity for ${item.name}`}
                    >
                      −
                    </button>
                    <span className="quantity-value">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.product_id, item.name, 1)}
                      className="quantity-button"
                      aria-label={`Increase quantity for ${item.name}`}
                    >
                      +
                    </button>
                  </div>
                  <button
                    onClick={() => handleRemove(item.product_id, item.name)}
                    className="remove-button"
                    aria-label={`Remove ${item.name}`}
                  >
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>
          {items.length > 0 && (
            <button
              className="checkout-button"
              onClick={handleCheckout}
              disabled={isProcessing}
              aria-busy={isProcessing}
            >
              {isProcessing ? "Processing..." : "Checkout"}
            </button>
          )}
        </div>
      </FocusTrap>
    </div>
  );
}
